/**
 * `/`: the next step, the referral link, the range's KPIs and chart, the
 * balance and the latest activity. Three reads (overview, performance for the
 * range, links), each with its own loading and error state.
 */
import { PageHeader } from "@nt/ui/page-header";

import { type QueryLike, QueryView } from "@/components/query-view";
import { RangePicker, useRangeDays } from "@/components/range-picker";
import { useGet, useSession } from "@/lib/queries";
import type {
  ComplianceStatus,
  LinksData,
  OverviewData,
  PerformanceData,
} from "@/lib/types";
import { CompliancePanel } from "@/pages/compliance-panel";
import {
  ActivityPanel,
  BalancePanel,
  NextStepBanner,
  ReferralLinkSection,
} from "@/pages/overview-panels";
import { ActivityChart } from "@/pages/performance/activity-chart";
import { KpiTiles } from "@/pages/performance/kpi-tiles";
import { usePerformance } from "@/pages/performance/use-performance";

export function OverviewView({
  overview,
  performance,
  links,
  compliance,
  readOnly = false,
}: {
  overview: QueryLike<OverviewData>;
  performance: QueryLike<PerformanceData>;
  links: QueryLike<LinksData>;
  /** Optional and silent on error: the panel only appears when there is something to fix. */
  compliance?: QueryLike<ComplianceStatus>;
  readOnly?: boolean;
}) {
  const pendingCents = overview.data?.balance.pendingCents ?? null;
  return (
    <>
      {overview.data ? <NextStepBanner step={overview.data.nextStep} /> : null}
      {compliance?.data ? (
        <CompliancePanel status={compliance.data} readOnly={readOnly} />
      ) : null}
      <ReferralLinkSection links={links} readOnly={readOnly} />
      <QueryView query={performance} what="your performance" rows={4}>
        {(data) => (
          <>
            <KpiTiles data={data} pendingCents={pendingCents} />
            <ActivityChart data={data} />
          </>
        )}
      </QueryView>
      <QueryView query={overview} what="your overview" rows={4}>
        {(data) => (
          <div className="grid items-start gap-6 lg:grid-cols-2">
            <BalancePanel data={data} />
            <ActivityPanel items={data.activity ?? []} />
          </div>
        )}
      </QueryView>
    </>
  );
}

export function Overview() {
  const [days, setDays] = useRangeDays();
  const overview = useGet<OverviewData>("overview", "/api/partner/overview");
  const links = useGet<LinksData>("links", "/api/partner/links");
  const compliance = useGet<ComplianceStatus>(
    "compliance",
    "/api/partner/compliance",
  );
  const performance = usePerformance(days);
  const session = useSession();
  return (
    <>
      <PageHeader
        title="Overview"
        description="How your links are doing and what you are owed."
        actions={<RangePicker value={days} onChange={setDays} />}
      />
      <OverviewView
        overview={overview}
        performance={performance}
        links={links}
        compliance={compliance}
        readOnly={session.data?.state === "read_only"}
      />
    </>
  );
}
